/**
 * discountMixin
 *
 * Shared mixin for discount-related calculations across product result pages.
 * Used by: osagoResults.vue, greencard/results.vue, tourism/results.vue, etc.
 */
export const discountMixin = {
    computed: {
        /**
         * Current user info (Vuex store). Components that already define `user`
         * computed property can override this without issue.
         */
        discountMixinUser() {
            return this.$store.getters.USER_INFO || {};
        },
    },

    methods: {
        /**
         * Calculate the minimum allowed sale price for an offer,
         * mirroring the `minDiscountPrice` logic in modal-discount-set.vue.
         *
         * Formula:
         *   totalFullPrice = floor(osagoPrice + dgoPrice + lawyerPrice)
         *   totalEarnings  = baseEarnings + dgoEarning + lawyerEarning
         *   netto          = ceil(max(1, totalFullPrice - totalEarnings))
         *   minPrice       = ceil(netto + totalEarnings * threshold / 100)
         *
         * @param {Object} offer - the offer object
         * @returns {number} minimum allowed sale price (integer)
         */
        showMinPrice(offer) {
            const basePrice = Number(offer.startPrice) || Number(offer.price) || 0;
            const dgo = Number(offer.dgoPrice || offer.dgo?.cost || offer.dgo?.price || 0);
            const lawyer = Number(offer.autolawyerPrice || offer.autolawyer?.price || 0);
            const totalFullPrice = Math.floor(basePrice + dgo + lawyer);

            const dgoEarning = Number(offer.dgoEarning || offer.dgo?.earning || 0);
            const lawyerEarning = Number(offer.autolawyerEarning || offer.autolawyer?.earning || 0);
            const baseEarnings = offer.startEarnings !== undefined
                ? Number(offer.startEarnings)
                : (Number(offer.earnings || 0) - dgoEarning - lawyerEarning);
            const totalEarnings = baseEarnings + dgoEarning + lawyerEarning;

            const netto = Math.ceil(Math.max(1, totalFullPrice - totalEarnings));
            // Use component's own `user` computed or the mixin's fallback
            const threshold = Number((this.user || this.discountMixinUser)?.discountLimitThreshold) || 0;

            let minPrice = netto;
            if (threshold > 0) {
                minPrice = Math.ceil(netto + (totalEarnings * threshold / 100));
            }

            return Math.min(minPrice, totalFullPrice);
        },
    },
};
